import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { ValidateEmailService } from '../signup-process-service/validate-email.service';

export interface AccountHolder {
  firstName: string;
  lastName: string;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class CustomerContextService {
  private readonly accountHolderSubject = new BehaviorSubject<AccountHolder | null>(null);
  readonly accountHolder$ = this.accountHolderSubject.asObservable();

  /** In-flight re-hydration request, shared so concurrent callers reuse it. */
  private pendingLoad$: Observable<AccountHolder | null> | null = null;

  constructor(private validateEmailService: ValidateEmailService) {}

  get snapshot(): AccountHolder | null {
    return this.accountHolderSubject.value;
  }

  setFromCustomer(customer: any): void {
    this.accountHolderSubject.next(this.toAccountHolder(customer));
  }

  ensureAccountHolder(): Observable<AccountHolder | null> {
    if (this.snapshot) {
      return of(this.snapshot);
    }
    if (this.pendingLoad$) {
      return this.pendingLoad$;
    }

    const email = this.getSignedInEmail();
    if (!email) {
      return of(null);
    }

    this.pendingLoad$ = this.validateEmailService.checkActiveEmail(email).pipe(
      map((customerData: any) => this.toAccountHolder(customerData?.customer)),
      tap((accountHolder) => {
        this.accountHolderSubject.next(accountHolder);
        this.pendingLoad$ = null;
      }),
      catchError((err) => {
        this.pendingLoad$ = null;
        return throwError(err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.pendingLoad$;
  }

  requireAccountHolder(): Observable<AccountHolder> {
    return this.ensureAccountHolder().pipe(
      filter((accountHolder): accountHolder is AccountHolder => !!accountHolder),
      take(1)
    );
  }

  clear(): void {
    this.pendingLoad$ = null;
    this.accountHolderSubject.next(null);
  }

  /**
   * The username is an identifier needed to re-authenticate the session, not a
   * profile record; it stays in `currentUser` alongside the token.
   */
  private getSignedInEmail(): string | null {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      return currentUser?.username ?? null;
    } catch {
      return null;
    }
  }

  private toAccountHolder(customer: any): AccountHolder | null {
    if (!customer) {
      return null;
    }
    return {
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: typeof customer.email === 'string' ? customer.email : customer.email?.email,
    };
  }
}
